/**
 * WHAT CLOSED WITHOUT A LETTER — the third view, and the one that had no surface at all.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠ WHY THIS EXISTS: TWO EMPTY LISTS WERE READ AS "NOT DUE"
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * The queue answers `state = 'drafted'`. The history answers `state = 'sent'`. A
 * completeness review ran both predicates in MySQL against every state the ledger writes
 * and found three of the five answering nothing on either surface:
 *
 *     drafted → the queue only        sent → the history only
 *     skipped / superseded / missed → 0 and 0
 *
 * Those three are written by the scan ON PURPOSE (`renewal-reminders.service.ts:496`).
 * `renewal-windows.ts` argues that `missed` exists "because its absence was a defect" — that
 * skipping silently made "the window passed and nobody was told" indistinguishable from "the
 * window passed and somebody was told". The ledger drew the distinction and then no screen
 * read it, so the defect came back one layer up: an operator opened both tabs, saw nothing
 * twice, and concluded the customer had never been due.
 *
 * That is standing rule 9 — "drafted but never sent must be VISIBLE rather than silent"
 * applies to EVERY state this feature can be in, not only the two that had a tab.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * ⚠ EVERY ROW SAYS WHICH KIND OF NOTHING, IN WORDS, AND THE THREE ARE NOT INTERCHANGEABLE
 * ═══════════════════════════════════════════════════════════════════════════════════════
 * They lead to opposite actions, which is the whole reason the ledger keeps them apart:
 *
 *   · `superseded` — the customer ALREADY RENEWED. Good news. Chasing them now contradicts
 *     the sale just made, so it is painted `info` and not as a warning; the register makes
 *     the same choice for the same reason.
 *   · `missed`     — the expiry passed before the scan next ran. NOBODY WAS TOLD, and
 *     nobody will be. This is the one somebody has to act on, so it is `warn`.
 *   · `skipped`    — a NEARER notice went instead. Nothing is wrong: a contract sold inside
 *     60 days crosses several windows at once and only the most urgent true statement is
 *     sent. Quiet, on the tint.
 *
 * ⚠ AND THERE IS NO "SENT TO" COLUMN, WHICH IS A FACT ABOUT THESE ROWS RATHER THAN A GAP.
 * The API returns no recipient because none was ever resolved — no letter was prepared — so
 * a permanently-empty column would read as "we do not know where it went" instead of
 * "nothing went anywhere".
 *
 * ⚠ COLUMN HEADERS ARE `text-fg`, AND THAT IS A MEASUREMENT, copied from `SentReminders`
 * for the reason recorded there: this `<tr>` sets its own `bg-subtle`, where BOTH dimmer
 * tokens fail AA in light mode — `text/muted` 2.49:1 and `text/secondary` 4.40:1 — against a
 * 4.5 floor for 11px/500 labels. `text/primary` on that tint is 16.14:1.
 */

import { Alert, EmptyState, SkeletonRows } from '../../components/ui';
import { formatDateOnly, formatSentAt } from './format';
import { PagerFooter } from './PagerFooter';
import { cn } from '../../lib/cn';
import { draftsErrorText } from './drafts-api';
import type { RenewalClosedHistoryPage, RenewalClosedRow, RenewalClosedState } from './drafts-api';

/**
 * The sentence for each state, and the tint that carries it.
 *
 * ⚠ EVERY PAIR HERE IS ALREADY A HOUSE PAIR WITH A MEASURED NUMBER. This screen authors NO
 * new colour: `info` is 4.53:1 in both modes, `warn` 4.51:1 in both, and `text-fg` on
 * `bg-subtle` 16.14 light / 15.11 dark. `sent-history.contrast.test.ts` pins all three, so
 * substituting a dimmer token later reddens rather than merely looking quieter.
 */
const CLOSED_COPY: Record<RenewalClosedState, { label: string; detail: string; tone: string }> = {
  superseded: {
    label: 'Already renewed',
    detail: 'A later contract for the same product replaced this one, so the chase stopped.',
    tone: 'bg-info-bg text-info-fg',
  },
  missed: {
    label: 'Nobody was told',
    detail: 'The expiry passed before this window could be prepared. No letter went out.',
    tone: 'bg-warn-bg text-warn-fg',
  },
  skipped: {
    label: 'A nearer notice went instead',
    detail: 'This contract crossed several windows at once; only the most urgent was sent.',
    tone: 'bg-subtle text-fg ring-1 ring-line',
  },
};

export function ClosedReminders({
  countryCode,
  page,
  isPending,
  isError,
  error,
  onRetry,
  onPage,
  partyFilter,
  onClearParty,
}: {
  countryCode: string;
  page: RenewalClosedHistoryPage | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  onPage: (next: number) => void;
  partyFilter: string | null;
  onClearParty: () => void;
}): React.JSX.Element {
  if (isPending) {
    return (
      <div className="rounded-md border border-line bg-surface px-4">
        <SkeletonRows rows={4} label="Loading closed reminder windows" />
      </div>
    );
  }
  if (isError || page === undefined) {
    return (
      <Alert tone="danger" title="The closed reminder windows could not be loaded">
        <p>{draftsErrorText(error)}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Try again.
        </button>
      </Alert>
    );
  }

  const rows = page.data;

  /* An empty PAGE is not an empty list — the U5.5 defect, on a third surface. */
  if (rows.length === 0 && page.total > 0) {
    return (
      <Alert tone="info" title={`Page ${String(page.page)} is past the end of this list`}>
        <p>
          {String(page.total)} {page.total === 1 ? 'window has' : 'windows have'} closed without a
          letter.
        </p>
        <button
          type="button"
          onClick={() => onPage(1)}
          className="mt-1 font-medium underline underline-offset-2"
        >
          Show the first page.
        </button>
      </Alert>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-line bg-surface">
        {partyFilter === null ? (
          <EmptyState
            title="No reminder window has closed without a letter in this country."
            description="Every window the scan has reached either produced a draft or has already been emailed. This list fills up when a customer renews early, when an expiry passes before the scan runs, or when a nearer notice goes instead."
          />
        ) : (
          <EmptyState
            title="No window has closed without a letter for this customer."
            description="Nothing about this customer was decided and dropped. Other customers may have windows here — this list is filtered."
            action={
              <button
                type="button"
                onClick={onClearParty}
                className="text-body13 font-medium underline underline-offset-2"
                data-testid="clear-party-filter-closed"
              >
                Show everything closed in {countryCode}.
              </button>
            }
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-md border border-line bg-surface">
        <table
          className="w-full min-w-[900px] border-collapse text-left"
          data-testid="closed-table"
        >
          <thead>
            <tr className="h-10 border-b border-line bg-subtle">
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Decided
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Customer
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Product
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Notice
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                Expiry stated
              </th>
              <th scope="col" className="px-3 text-th uppercase text-fg">
                What happened
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <ClosedRowCells key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
      <PagerFooter
        page={page.page}
        perPage={page.perPage}
        shown={rows.length}
        total={page.total}
        onPage={onPage}
      />
    </>
  );
}

function ClosedRowCells({ row }: { row: RenewalClosedRow }): React.JSX.Element {
  const copy = CLOSED_COPY[row.state];
  return (
    <tr
      data-testid="closed-row"
      data-closed-id={row.id}
      data-state={row.state}
      className="crf-register-row border-b border-line last:border-b-0"
    >
      <td className="tnum px-3 py-2 text-body13 text-fg">{formatSentAt(row.created_at)}</td>
      {/*
        ⚠ THE CUSTOMER IS TEXT, NOT A LINK, AND THAT IS DELIBERATE. `/countries/:cc/parties/:id`
        is not a route — `domain/nav.ts:327` marks the parties entry `available: false` and no
        feature mounts the path, so a link there resolves to the Not-found catch-all. This
        repo's own rule, stated twice in this feature: "a dead link is worse than no link
        because the operator concludes the document is missing".
      */}
      <td className="px-3 py-2 text-body13 font-medium text-fg">
        {row.party_name ?? 'Customer record removed'}
      </td>
      <td className="px-3 py-2 text-body13 text-fg">
        {row.product_name ?? 'Product record removed'}
      </td>
      <td className="px-3 py-2 text-body13 text-fg">{row.window_days}-day</td>
      <td className="tnum px-3 py-2 text-body13 text-fg">{formatDateOnly(row.expiry_date)}</td>
      <td className="px-3 py-2 text-body13">
        <span
          data-testid="closed-state"
          className={cn(
            'inline-flex h-6 items-center rounded-pill px-2 text-body13',
            copy.tone,
          )}
        >
          {copy.label}
        </span>
        {/* The reason IN WORDS beside the chip: a label alone is a category, not an answer. */}
        <span className="ml-2 text-fg-secondary">{copy.detail}</span>
      </td>
    </tr>
  );
}
